<script lang="ts" setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from "vue"

const props = withDefaults(
  defineProps<{
    data: { url: string; caption?: string }
    isVisible: boolean
    isActive?: boolean
  }>(),
  { isActive: false },
)

const emit = defineEmits<{
  heightChange: [height: number]
}>()

interface OEmbedJson {
  html: string
  thumbnail_url: string
  title: string
}

const json = ref<OEmbedJson | null>(null)
const modalOpen = ref(false)
const thumbnail = ref<HTMLImageElement>()

// Report the rendered (object-contain) thumbnail height so the gallery can hug
// it with the caption, like images and videos.
const onResize = () => {
  if (!thumbnail.value || !props.isActive) return
  const renderedHeight = thumbnail.value.getBoundingClientRect().height
  if (renderedHeight > 0) emit("heightChange", renderedHeight)
}

watch(
  () => props.isActive,
  async value => {
    if (value) {
      await nextTick()
      onResize()
    }
  },
)

const fetchEmbed = async () => {
  if (json.value) return
  try {
    const res = await fetch(
      `https://sketchfab.com/oembed?url=${props.data.url}`,
    )
    json.value = await res.json()
  } catch (e) {
    console.error("Failed to fetch Sketchfab oEmbed:", e)
  }
}

watch(
  () => props.isVisible,
  visible => {
    if (visible) fetchEmbed()
  },
  { immediate: true },
)

const open = () => {
  modalOpen.value = true
  document.body.classList.add("overflow-hidden")
}

const close = () => {
  modalOpen.value = false
  document.body.classList.remove("overflow-hidden")
}

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape") close()
}

onMounted(() => {
  window.addEventListener("keydown", onKeydown)
  window.addEventListener("resize", onResize)
})
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown)
  window.removeEventListener("resize", onResize)
  document.body.classList.remove("overflow-hidden")
})
</script>

<template>
  <div class="relative flex items-center justify-center w-full h-full" data-slide-content="sketchfab">
    <template v-if="json">
      <img
        ref="thumbnail"
        :src="json.thumbnail_url"
        :alt="json.title"
        draggable="false"
        class="select-none object-contain w-full h-full"
        @load="onResize"
      />
      <button
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer hover:opacity-100 transition-opacity bg-black rounded-full p-2"
        @click="open"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="white"
          class="w-12 h-12"
        >
          <path
            d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
          />
        </svg>
      </button>
    </template>
  </div>

  <Teleport to="body">
    <div
      v-if="modalOpen"
      class="fixed inset-0 z-200 bg-black/90 flex items-center justify-center"
    >
      <button
        class="absolute top-4 right-4 text-white text-3xl leading-none cursor-pointer hover:opacity-70 transition-opacity"
        @click="close"
        aria-label="Close"
      >
        ×
      </button>
      <div
        class="w-[90vw] aspect-video [&>div]:w-full [&>div]:h-full [&_iframe]:w-full! [&_iframe]:h-full!"
        v-html="json?.html"
      ></div>
    </div>
  </Teleport>
</template>
